import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { eventOsApi } from "../../../api";
import { queryKeys } from "../../../queryKeys";
import { EventOSData, UiStatus } from "../../../types";
import type { WorkspaceArea } from "../types";

const emptyData: EventOSData = {
  eventName: "No event selected",
  eventSubtitle: "Connect APIs to populate event details",
  eventHealthScore: null,
  overviewMetrics: [],
  pnlMetrics: [],
  ticketMetrics: [],
  venueMetrics: [],
  workforceMetrics: [],
  vendorMetrics: [],
  taskMetrics: [],
  riskMetrics: [],
  liveMetrics: [],
  ticketTiers: [],
  scenarioMetrics: [],
  reportMetrics: [],
  alerts: [],
  insights: [],
  ticketTable: { headers: [], rows: [] },
  workforceTable: { headers: [], rows: [] },
  vendorTable: { headers: [], rows: [] },
  riskTable: { headers: [], rows: [] },
  revenueChart: [],
  expenseChart: [],
  ticketTierChart: [],
  scenarioChart: [],
};

/** Event OS workspace payload; cached per event + token. */
export function useEventOSData(token: string | null, eventId: string | null, workspace: WorkspaceArea) {
  const q = useQuery({
    queryKey:
      eventId && token ? queryKeys.eventOs.workspace(workspace, eventId) : queryKeys.eventOs.workspaceIdle(workspace),
    queryFn: () => eventOsApi.getDashboardData(eventId!, token ?? undefined),
    enabled: Boolean(token && eventId),
  });

  const status: UiStatus = useMemo(() => {
    if (!eventId) return "ready";
    if (q.isPending) return "loading";
    if (q.isError) return "error";
    return "ready";
  }, [eventId, q.isPending, q.isError]);

  const data = q.data ?? emptyData;
  const error = q.error instanceof Error ? q.error.message : q.error ? String(q.error) : null;

  return useMemo(
    () => ({
      status,
      error,
      data,
      refetch: q.refetch,
      isFetching: q.isFetching,
    }),
    [status, error, data, q.refetch, q.isFetching]
  );
}
